import { useState, useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ChevronLeft, Sparkles, ShoppingCart, Download, Mail, Printer,
  Check, ChevronDown, ChevronUp, AlertCircle, Share2, Loader2,
  X, Plus, Layers,
} from 'lucide-react'
import { api } from '@/lib/api'
import StoreSelector from '@/components/StoreSelector'
import { DEPARTMENT_LABELS, DEPARTMENT_ORDER, STORE_TYPE_LABELS, formatDate } from '@/lib/utils'
import type { FullWeeklyPlan, IngredientItem, ShoppingItem, ShoppingList, StoreGroup, ShoppingSection } from '@/types/models'

interface Props {
  planId: string
  onBack: () => void
}

type Phase = 'ingredients' | 'stores' | 'list'

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default function ShoppingListPage({ planId, onBack }: Props) {
  const { getToken } = useAuth()
  const [phase, setPhase] = useState<Phase>('ingredients')
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([])
  const [ingredientListId, setIngredientListId] = useState<string | null>(null)
  const [shoppingList, setShoppingList] = useState<ShoppingList | null>(null)
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set())
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const [exportMsg, setExportMsg] = useState<string | null>(null)

  // ── Editing state ──────────────────────────────────────────────────────────
  const [showCombined, setShowCombined] = useState(false)
  const [addingToStore, setAddingToStore] = useState<string | null>(null)
  const [newItemName, setNewItemName] = useState('')
  const [newItemQty, setNewItemQty] = useState('')
  const [newItemUnit, setNewItemUnit] = useState('')
  const [editQty, setEditQty] = useState<{ key: string; val: string } | null>(null)

  const { data: planRes } = useQuery({
    queryKey: ['plan', planId],
    queryFn: async () => {
      const token = await getToken()
      return api.get<{ data: FullWeeklyPlan }>(`/plans/${planId}`, token!)
    },
  })
  const plan = (planRes as { data: FullWeeklyPlan } | undefined)?.data

  // Phase 1: Generate ingredient list
  const generateIngredients = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      return api.post<{ data: { list_id: string; items: IngredientItem[] } }>(
        '/ai/generate-ingredients', { plan_id: planId }, token!
      )
    },
    onSuccess: (res) => {
      const r = res as { data: { list_id: string; items: IngredientItem[] } }
      setIngredientListId(r.data.list_id)
      setPhase('stores')
    },
  })

  // Phase 3: Generate shopping list
  const generateShoppingList = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      return api.post<{ data: ShoppingList }>(
        '/ai/generate-shopping-list',
        { plan_id: planId, ingredient_list_id: ingredientListId, store_ids: selectedStoreIds },
        token!
      )
    },
    onSuccess: (res) => {
      const r = res as { data: ShoppingList }
      setShoppingList(r.data)
      setPhase('list')
    },
  })

  // ── List mutation helpers ──────────────────────────────────────────────────

  function removeItem(storeId: string, dept: string, ingredient: string) {
    setShoppingList(prev => {
      if (!prev) return prev
      return {
        ...prev,
        store_groups: prev.store_groups.map(sg =>
          sg.store_id !== storeId ? sg : {
            ...sg,
            sections: sg.sections
              .map(sec => sec.department !== dept ? sec : {
                ...sec,
                items: sec.items.filter(i => i.ingredient !== ingredient),
              })
              .filter(sec => sec.items.length > 0),
          }
        ),
      }
    })
    setCheckedItems(prev => {
      const next = new Set(prev)
      next.delete(`${storeId}:${dept}:${ingredient}`)
      return next
    })
  }

  // Remove from every store (used in combined view)
  function removeCombinedItem(dept: string, ingredient: string) {
    setShoppingList(prev => {
      if (!prev) return prev
      return {
        ...prev,
        store_groups: prev.store_groups.map(sg => ({
          ...sg,
          sections: sg.sections
            .map(sec => sec.department !== dept ? sec : {
              ...sec,
              items: sec.items.filter(i => i.ingredient.toLowerCase() !== ingredient.toLowerCase()),
            })
            .filter(sec => sec.items.length > 0),
        })),
      }
    })
  }

  function addCustomItem(storeId: string) {
    const name = newItemName.trim()
    if (!name) return
    const qty = parseFloat(newItemQty) || 1
    const unit = newItemUnit.trim() || 'item'
    setShoppingList(prev => {
      if (!prev) return prev
      return {
        ...prev,
        store_groups: prev.store_groups.map(sg => {
          if (sg.store_id !== storeId) return sg
          const hasOther = sg.sections.some(s => s.department === 'other')
          const newItem: ShoppingItem = {
            ingredient: name, quantity: qty, unit,
            checked: false, notes: '',
          }
          return {
            ...sg,
            sections: hasOther
              ? sg.sections.map(sec =>
                  sec.department !== 'other' ? sec : { ...sec, items: [...sec.items, newItem] }
                )
              : [...sg.sections, { department: 'other', items: [newItem] }],
          }
        }),
      }
    })
    setNewItemName('')
    setNewItemQty('')
    setNewItemUnit('')
    setAddingToStore(null)
  }

  function commitQtyEdit(storeId: string, dept: string, ingredient: string) {
    if (!editQty) return
    const qty = parseFloat(editQty.val)
    if (!isNaN(qty) && qty > 0) {
      setShoppingList(prev => {
        if (!prev) return prev
        return {
          ...prev,
          store_groups: prev.store_groups.map(sg =>
            sg.store_id !== storeId ? sg : {
              ...sg,
              sections: sg.sections.map(sec =>
                sec.department !== dept ? sec : {
                  ...sec,
                  items: sec.items.map(i =>
                    i.ingredient !== ingredient ? i : { ...i, quantity: qty }
                  ),
                }
              ),
            }
          ),
        }
      })
    }
    setEditQty(null)
  }

  // ── Combined list (all stores merged, grouped by department) ──────────────
  const combinedSections = useMemo(() => {
    if (!shoppingList) return []
    const deptMap = new Map<string, Map<string, { ingredient: string; quantity: number; unit: string }>>()
    for (const sg of shoppingList.store_groups) {
      for (const sec of sg.sections) {
        if (!deptMap.has(sec.department)) deptMap.set(sec.department, new Map())
        const itemMap = deptMap.get(sec.department)!
        for (const item of sec.items) {
          const k = `${item.ingredient.toLowerCase()}||${item.unit.toLowerCase()}`
          const existing = itemMap.get(k)
          if (existing) {
            existing.quantity = Math.round((existing.quantity + item.quantity) * 100) / 100
          } else {
            itemMap.set(k, { ingredient: item.ingredient, quantity: item.quantity, unit: item.unit })
          }
        }
      }
    }
    return Array.from(deptMap.entries())
      .map(([dept, items]) => ({ department: dept, items: Array.from(items.values()) }))
      .sort((a, b) => {
        const ai = DEPARTMENT_ORDER.indexOf(a.department)
        const bi = DEPARTMENT_ORDER.indexOf(b.department)
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
      })
  }, [shoppingList])

  // ── Misc helpers ──────────────────────────────────────────────────────────

  function toggleStore(id: string) {
    setSelectedStoreIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  function toggleItem(key: string) {
    setCheckedItems(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  function toggleSection(key: string) {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  function getSortedSections(sections: ShoppingSection[]) {
    return [...sections].sort((a, b) => {
      const ai = DEPARTMENT_ORDER.indexOf(a.department)
      const bi = DEPARTMENT_ORDER.indexOf(b.department)
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
    })
  }

  // ── Print / export ────────────────────────────────────────────────────────

  function buildPrintHtml(): string {
    if (!shoppingList || !plan) return ''
    const weekOf = plan.week_start_date ? formatDate(plan.week_start_date) : ''
    const groupSize = plan.default_group_size ?? 1
    const daysCount = plan.days_count ?? (plan.days?.length ?? 0)
    const printDate = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

    const mealPlanHtml = (plan.days ?? []).map(day => {
      const nonSkipped = (day.slots ?? []).filter(s => !s.is_skipped && s.dish_name)
      const dateLabel = formatDate(day.date)
      if (day.is_eating_out) {
        return `<div class="day-block"><div class="day-label">${escapeHtml(dateLabel)}</div><div class="meal-item eating-out">Eating out</div></div>`
      }
      if (nonSkipped.length === 0) return ''
      const mealsHtml = nonSkipped.map(s =>
        `<div class="meal-item"><span class="slot-label">${escapeHtml(s.label ?? `Meal ${s.slot_index + 1}`)}:</span> ${escapeHtml(s.dish_name ?? '')}</div>`
      ).join('')
      return `<div class="day-block"><div class="day-label">${escapeHtml(dateLabel)}</div>${mealsHtml}</div>`
    }).join('')

    const shoppingHtml = shoppingList.store_groups.map(sg => {
      const storeLabel = STORE_TYPE_LABELS[sg.store_type] ?? sg.store_type
      const sectionsHtml = getSortedSections(sg.sections).map(sec => {
        const deptLabel = DEPARTMENT_LABELS[sec.department] ?? sec.department
        const itemsHtml = sec.items.map(i => {
          const key = `${sg.store_id}:${sec.department}:${i.ingredient}`
          const checked = checkedItems.has(key)
          return `<div class="shop-item"><span><span class="checkbox${checked ? ' checked' : ''}"></span>${escapeHtml(i.ingredient)}</span><span class="qty">${i.quantity} ${escapeHtml(i.unit)}</span></div>`
        }).join('')
        return `<div class="dept-section"><div class="dept-name">${escapeHtml(deptLabel)}</div>${itemsHtml}</div>`
      }).join('')
      return `<div class="store-block"><div class="store-name">&#x1F6D2; ${escapeHtml(sg.store_name)} <span class="store-type">(${escapeHtml(storeLabel)})</span></div>${sectionsHtml}</div>`
    }).join('')

    return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>MealMap Shopping List</title>
<style>*{box-sizing:border-box;margin:0;padding:0}@page{size:letter portrait;margin:.38in}html{font-family:Arial,sans-serif;font-size:8pt;color:#1a1a1a}body{width:7.74in}#wrap{width:100%}.print-header{border-bottom:2px solid #2c2c2c;padding-bottom:5px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:flex-end}.print-header h1{font-size:13pt;font-weight:bold}.print-header .meta{font-size:7.5pt;color:#666;margin-top:2px}.print-header .date{font-size:7pt;color:#999;text-align:right}.columns{display:grid;grid-template-columns:34% 64%;gap:2%}.col-header{font-size:9pt;font-weight:bold;border-bottom:1px solid #bbb;padding-bottom:3px;margin-bottom:5px}.day-block{margin-bottom:5px}.day-label{font-size:7.5pt;font-weight:bold;color:#333;text-transform:uppercase}.meal-item{font-size:7pt;padding-left:7px;line-height:1.5;color:#444}.slot-label{color:#888}.eating-out{font-style:italic;color:#999}.store-block{margin-bottom:8px}.store-name{font-size:8pt;font-weight:bold;background:#efefef;padding:2px 5px;border-left:3px solid #555}.store-type{font-weight:normal;font-size:7pt;color:#777}.dept-section{margin:4px 0}.dept-name{font-size:7.5pt;font-weight:bold;color:#555;padding:2px 0 1px;border-bottom:1px dotted #ddd;margin-bottom:2px}.shop-item{font-size:7pt;display:flex;justify-content:space-between;padding:1px 0 1px 8px;line-height:1.5}.qty{color:#666;white-space:nowrap;padding-left:4px}.checkbox{display:inline-block;width:7px;height:7px;border:1px solid #777;margin-right:3px;vertical-align:middle;position:relative;top:-1px}.checkbox.checked{background:#2c2c2c}</style>
</head><body><div id="wrap">
  <div class="print-header"><div><h1>MealMap &mdash; Shopping List &amp; Meal Plan</h1><div class="meta">Week of ${escapeHtml(weekOf)} &bull; ${groupSize} ${groupSize === 1 ? 'person' : 'people'} &bull; ${daysCount} days</div></div><div class="date">Printed ${escapeHtml(printDate)}</div></div>
  <div class="columns"><div class="meal-plan-col"><div class="col-header">&#x1F4C5; Meal Plan</div>${mealPlanHtml || '<div class="meal-item eating-out">No meals recorded</div>'}</div><div class="shopping-col"><div class="col-header">&#x1F6D2; Shopping List</div>${shoppingHtml}</div></div>
</div><script>window.onload=function(){var w=document.getElementById('wrap'),pH=10.24*96,cH=w.offsetHeight;if(cH>pH){var s=pH/cH;w.style.transform='scale('+s+')';w.style.transformOrigin='top left';w.style.width=Math.round(100/s)+'%'}setTimeout(function(){window.print()},150)}</script></body></html>`
  }

  function buildListText(): string {
    if (!shoppingList) return ''
    return shoppingList.store_groups.map(sg => {
      const header = `\n🛒 ${sg.store_name.toUpperCase()} (${STORE_TYPE_LABELS[sg.store_type]})\n${'─'.repeat(40)}`
      const sections = getSortedSections(sg.sections).map(sec => {
        const deptLabel = DEPARTMENT_LABELS[sec.department] ?? sec.department
        const items = sec.items.map(i => {
          const key = `${sg.store_id}:${sec.department}:${i.ingredient}`
          return `  [${checkedItems.has(key) ? 'x' : ' '}] ${i.quantity} ${i.unit} ${i.ingredient}`
        }).join('\n')
        return `\n${deptLabel}\n${items}`
      }).join('\n')
      return header + sections
    }).join('\n\n')
  }

  function handlePrint() {
    if (!shoppingList || !plan) return
    const w = window.open('', '_blank', 'width=900,height=700')
    if (!w) return
    w.document.open()
    w.document.write(buildPrintHtml())
    w.document.close()
  }

  async function handleEmailExport() {
    const text = buildListText()
    window.location.href = `mailto:?subject=MealMap Shopping List – Week of ${plan?.week_start_date}&body=${encodeURIComponent(text)}`
  }

  function handleSaveImage() {
    setExportMsg('Save as image: Right-click and choose Print → Save as PDF, or use your browser\'s screenshot tool.')
    setTimeout(() => setExportMsg(null), 6000)
  }

  const allItems = shoppingList?.store_groups.flatMap(sg =>
    sg.sections.flatMap(sec => sec.items.map(i => `${sg.store_id}:${sec.department}:${i.ingredient}`))
  ) ?? []
  const checkedCount = allItems.filter(k => checkedItems.has(k)).length

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="btn-ghost p-2">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shopping List</h1>
          {plan && <p className="text-sm text-gray-500 mt-0.5">Week of {formatDate(plan.week_start_date)}</p>}
        </div>
      </div>

      {/* Phase stepper */}
      <div className="flex items-center gap-2">
        {(['ingredients', 'stores', 'list'] as Phase[]).map((p, i) => {
          const labels = ['Generate Ingredients', 'Select Stores', 'Shopping List']
          const done = phase === 'stores' && i === 0 || phase === 'list' && i <= 1
          const active = phase === p
          return (
            <div key={p} className="flex items-center gap-2 flex-1">
              <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                done ? 'bg-green-500 text-white' : active ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-400'
              }`}>
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${active ? 'text-brand-600' : done ? 'text-green-600' : 'text-gray-400'}`}>
                {labels[i]}
              </span>
              {i < 2 && <div className="flex-1 h-px bg-gray-200" />}
            </div>
          )
        })}
      </div>

      {/* Phase 1 */}
      {phase === 'ingredients' && (
        <div className="card p-6 text-center space-y-4">
          <div className="w-14 h-14 bg-brand-100 rounded-full flex items-center justify-center mx-auto">
            <Sparkles className="w-7 h-7 text-brand-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Generate Ingredient List</h2>
            <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
              AI will analyze your meal plan and calculate all the ingredients you need — with quantities
              scaled to your group size of <strong>{plan?.default_group_size ?? '?'}</strong>.
            </p>
          </div>
          {generateIngredients.isError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Failed to generate. Please check your plan has meals filled in.
            </div>
          )}
          <button
            onClick={() => generateIngredients.mutate()}
            disabled={generateIngredients.isPending}
            className="btn-primary inline-flex items-center gap-2"
          >
            {generateIngredients.isPending
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing your meals…</>
              : <><Sparkles className="w-4 h-4" /> Generate Ingredient List</>}
          </button>
        </div>
      )}

      {/* Phase 2 */}
      {phase === 'stores' && (
        <div className="card p-6 space-y-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Select Your Stores</h2>
            <p className="text-sm text-gray-500 mt-1">
              Choose one or more stores. AI will group ingredients by store and department.
            </p>
          </div>
          <StoreSelector selectedStoreIds={selectedStoreIds} onToggle={toggleStore} />
          {generateShoppingList.isError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              List generation failed. Please try again.
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setPhase('ingredients')} className="btn-secondary">← Back</button>
            <button
              onClick={() => generateShoppingList.mutate()}
              disabled={selectedStoreIds.length === 0 || generateShoppingList.isPending}
              className="btn-primary flex items-center gap-2"
            >
              {generateShoppingList.isPending
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Building list…</>
                : <><ShoppingCart className="w-4 h-4" /> Generate Shopping List</>}
            </button>
          </div>
        </div>
      )}

      {/* Phase 3: The List */}
      {phase === 'list' && shoppingList && (
        <div className="space-y-4">

          {/* Export / view bar */}
          <div className="card px-4 py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <p className="text-sm font-medium text-gray-700">
                {checkedCount} / {allItems.length} items checked
              </p>
              <button
                onClick={() => setShowCombined(v => !v)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                  showCombined
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-brand-400 hover:text-brand-600'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                Combined list
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleSaveImage} className="btn-ghost flex items-center gap-1.5 text-xs px-3 py-2">
                <Download className="w-3.5 h-3.5" /> Save
              </button>
              <button onClick={handleEmailExport} className="btn-ghost flex items-center gap-1.5 text-xs px-3 py-2">
                <Mail className="w-3.5 h-3.5" /> Email
              </button>
              <button onClick={handlePrint} className="btn-ghost flex items-center gap-1.5 text-xs px-3 py-2">
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              {shoppingList.share_token && (
                <button onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/list/${shoppingList.share_token}`)
                  setExportMsg('Link copied to clipboard!')
                  setTimeout(() => setExportMsg(null), 3000)
                }} className="btn-ghost flex items-center gap-1.5 text-xs px-3 py-2">
                  <Share2 className="w-3.5 h-3.5" /> Share
                </button>
              )}
            </div>
          </div>

          {exportMsg && (
            <div className="bg-brand-50 border border-brand-200 text-brand-700 text-sm rounded-xl px-4 py-3">
              {exportMsg}
            </div>
          )}

          {/* ── Combined view ─────────────────────────────────────────────── */}
          {showCombined && (
            <div className="card overflow-hidden">
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <Layers className="w-4 h-4 text-brand-600" />
                <h3 className="font-bold text-gray-900">All Items — Combined</h3>
                <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                  {combinedSections.reduce((n, s) => n + s.items.length, 0)} items across{' '}
                  {shoppingList.store_groups.length}{' '}
                  {shoppingList.store_groups.length === 1 ? 'store' : 'stores'}
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {combinedSections.map(sec => {
                  const sectionKey = `combined:${sec.department}`
                  const isCollapsed = collapsedSections.has(sectionKey)
                  const deptLabel = DEPARTMENT_LABELS[sec.department] ?? sec.department
                  const deptChecked = sec.items.filter(i =>
                    checkedItems.has(`combined:${sec.department}:${i.ingredient}`)
                  ).length
                  return (
                    <div key={sec.department}>
                      <button
                        onClick={() => toggleSection(sectionKey)}
                        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700">{deptLabel}</span>
                          <span className="text-xs text-gray-400">({sec.items.length})</span>
                          {deptChecked > 0 && (
                            <span className="text-xs text-green-600 font-medium">
                              {deptChecked}/{sec.items.length} ✓
                            </span>
                          )}
                        </div>
                        {isCollapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
                      </button>
                      {!isCollapsed && (
                        <div className="px-5 pb-3 space-y-1">
                          {sec.items.map(item => {
                            const itemKey = `combined:${sec.department}:${item.ingredient}`
                            const checked = checkedItems.has(itemKey)
                            return (
                              <div
                                key={itemKey}
                                className={`flex items-center justify-between py-2 px-3 rounded-lg group transition-colors ${checked ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <button
                                    onClick={() => toggleItem(itemKey)}
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                      checked ? 'border-green-500 bg-green-500' : 'border-gray-300'
                                    }`}
                                  >
                                    {checked && <Check className="w-3 h-3 text-white" />}
                                  </button>
                                  <span className={`text-sm truncate ${checked ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                    {item.ingredient}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                  <span className={`text-xs font-medium ${checked ? 'text-gray-300' : 'text-gray-500'}`}>
                                    {item.quantity} {item.unit}
                                  </span>
                                  <button
                                    onClick={() => removeCombinedItem(sec.department, item.ingredient)}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 transition-all"
                                    title="Remove item"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* ── Per-store view ─────────────────────────────────────────────── */}
          {!showCombined && shoppingList.store_groups.map((sg: StoreGroup) => (
            <div key={sg.store_id} className="card overflow-hidden">
              {/* Store header */}
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-brand-600" />
                  <h3 className="font-bold text-gray-900">{sg.store_name}</h3>
                  <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                    {STORE_TYPE_LABELS[sg.store_type]}
                  </span>
                </div>
              </div>

              {/* Departments */}
              <div className="divide-y divide-gray-100">
                {getSortedSections(sg.sections).map((sec: ShoppingSection) => {
                  const sectionKey = `${sg.store_id}:${sec.department}`
                  const isCollapsed = collapsedSections.has(sectionKey)
                  const deptLabel = DEPARTMENT_LABELS[sec.department] ?? sec.department
                  const deptChecked = sec.items.filter(i =>
                    checkedItems.has(`${sg.store_id}:${sec.department}:${i.ingredient}`)
                  ).length

                  return (
                    <div key={sec.department}>
                      <button
                        onClick={() => toggleSection(sectionKey)}
                        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700">{deptLabel}</span>
                          <span className="text-xs text-gray-400">({sec.items.length})</span>
                          {deptChecked > 0 && (
                            <span className="text-xs text-green-600 font-medium">
                              {deptChecked}/{sec.items.length} ✓
                            </span>
                          )}
                        </div>
                        {isCollapsed
                          ? <ChevronDown className="w-4 h-4 text-gray-400" />
                          : <ChevronUp className="w-4 h-4 text-gray-400" />}
                      </button>

                      {!isCollapsed && (
                        <div className="px-5 pb-3 space-y-1">
                          {sec.items.map((item) => {
                            const itemKey = `${sg.store_id}:${sec.department}:${item.ingredient}`
                            const checked = checkedItems.has(itemKey)
                            const isEditingThis = editQty?.key === itemKey

                            return (
                              <div
                                key={itemKey}
                                className={`flex items-center justify-between py-2 px-3 rounded-lg group transition-colors ${
                                  checked ? 'bg-green-50' : 'hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <button
                                    onClick={() => toggleItem(itemKey)}
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                      checked ? 'border-green-500 bg-green-500' : 'border-gray-300'
                                    }`}
                                  >
                                    {checked && <Check className="w-3 h-3 text-white" />}
                                  </button>
                                  <span className={`text-sm truncate ${checked ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                    {item.ingredient}
                                  </span>
                                </div>

                                {/* Quantity — click to edit inline */}
                                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                  {isEditingThis ? (
                                    <div className="flex items-center gap-1">
                                      <input
                                        autoFocus
                                        type="number"
                                        min="0.01"
                                        step="any"
                                        value={editQty.val}
                                        onChange={e => setEditQty({ key: itemKey, val: e.target.value })}
                                        onBlur={() => commitQtyEdit(sg.store_id, sec.department, item.ingredient)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') commitQtyEdit(sg.store_id, sec.department, item.ingredient)
                                          if (e.key === 'Escape') setEditQty(null)
                                        }}
                                        className="w-16 text-xs border border-brand-400 rounded px-1.5 py-0.5 text-right focus:outline-none focus:ring-1 focus:ring-brand-500"
                                      />
                                      <span className="text-xs text-gray-500">{item.unit}</span>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setEditQty({ key: itemKey, val: String(item.quantity) })}
                                      className={`text-xs font-medium px-1.5 py-0.5 rounded hover:bg-brand-100 hover:text-brand-700 transition-colors ${
                                        checked ? 'text-gray-300' : 'text-gray-500'
                                      }`}
                                      title="Click to edit quantity"
                                    >
                                      {item.quantity} {item.unit}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => removeItem(sg.store_id, sec.department, item.ingredient)}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 transition-all"
                                    title="Remove item"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Add item — per store */}
              <div className="border-t border-gray-100 px-5 py-3">
                {addingToStore === sg.store_id ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      autoFocus
                      type="text"
                      placeholder="Item name"
                      value={newItemName}
                      onChange={e => setNewItemName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomItem(sg.store_id)}
                      className="input flex-1 min-w-32 text-sm py-1.5"
                    />
                    <input
                      type="number"
                      min="0.01"
                      step="any"
                      placeholder="Qty"
                      value={newItemQty}
                      onChange={e => setNewItemQty(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomItem(sg.store_id)}
                      className="input w-16 text-sm py-1.5 text-center"
                    />
                    <input
                      type="text"
                      placeholder="Unit"
                      value={newItemUnit}
                      onChange={e => setNewItemUnit(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomItem(sg.store_id)}
                      className="input w-16 text-sm py-1.5"
                    />
                    <button
                      onClick={() => addCustomItem(sg.store_id)}
                      disabled={!newItemName.trim()}
                      className="btn-primary text-sm py-1.5 px-3 disabled:opacity-50"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setAddingToStore(null)
                        setNewItemName('')
                        setNewItemQty('')
                        setNewItemUnit('')
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setAddingToStore(sg.store_id)
                      setNewItemName('')
                      setNewItemQty('')
                      setNewItemUnit('')
                    }}
                    className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-brand-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add item to {sg.store_name}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Regenerate option */}
          <div className="text-center">
            <button
              onClick={() => { setShoppingList(null); setPhase('stores') }}
              className="text-sm text-gray-400 hover:text-brand-600 underline"
            >
              Change stores or regenerate list
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
